import React, { lazy, Suspense, useContext } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import PageLoader from '../components/Loader/PageLoader';
import Layout from '../Layout/Layout';
import { ThemeContext } from '../context/ThemeContext';
import { LinkProvider } from '../components/ui-x';

/* ui-x renders <UIXLink>; this teaches it what a link is in this app. */
const RouterLink = ({ to, children, ...rest }) =>
  /^(https?:)?\/\//.test(to) || to.startsWith('mailto:') ? (
    <a href={to} {...rest}>
      {children}
    </a>
  ) : (
    <Link to={to} {...rest}>
      {children}
    </Link>
  );
import MatchedJobsTable from 'src/pages/Dashboard/MatchedJobsTable';
import ActiveProjects from 'src/pages/Project/ActiveProjects';
import TransactionTable from 'src/pages/Wallet/TransactionTable';
import WalletConfirmation from 'src/pages/Wallet/confirmation';

const AllRoutes = () => {
  const NotFound = lazy(() => import('../pages/NotFound/NotFound'));
  const Overview = lazy(() => import('../pages/Dashboard/Overview'));
  const EditExpertise = lazy(() => import('../pages/Profile/EditExpertise'));
  const AllOffers = lazy(() => import('../pages/Dashboard/AllOffers'));
  const MyOffers = lazy(() => import('../pages/Dashboard/MyOffer'));
  const Invites = lazy(() => import('../pages/Dashboard/Invites'));
  const Invite = lazy(() => import('../pages/Dashboard/Invite'));
  const Profile = lazy(() => import('../pages/Profile/Profile'));
  const EditProfile = lazy(() => import('../pages/Profile/EditProfile'));
  const Project = lazy(() => import('../pages/Project/Project'));
  const ActiveProject = lazy(() => import('../pages/Project/ActiveProject'));
  const Wallet = lazy(() => import('../pages/Wallet/Wallet'));
  const Withdraw = lazy(() => import('../pages/Wallet/Withdraw'));
  const WithdrawalDetails = lazy(() =>
    import('../pages/Wallet/WithdrawalDetails')
  );
  const MatchedJobs = lazy(() => import('../pages/Dashboard/MatchedJobs'));
  const Test = lazy(() => import('../pages/Test/Test'));
  const TakeTest = lazy(() => import('../pages/Test/TakeTest'));

  const Support = lazy(() => import('../pages/Support/Support'));
  const ContentCreator = lazy(() => import('../pages/ContentCreator'))
  const ManageOnboardingHub = lazy(() =>
    import('../pages/Manage/Onboarding/OnboardingHub')
  );
  const ManagePersonalProfile = lazy(() =>
    import('../pages/Manage/Onboarding/PersonalProfile')
  );
  const ManageHomeAddress = lazy(() =>
    import('../pages/Manage/Onboarding/HomeAddress')
  );
  const ManageBusinessInformation = lazy(() =>
    import('../pages/Manage/Onboarding/BusinessInformation')
  );
  const ManageAdministrativeDetails = lazy(() =>
    import('../pages/Manage/Onboarding/AdministrativeDetails')
  );
  const ManageVerification = lazy(() =>
    import('../pages/Manage/Onboarding/Verification')
  );
  const ManageDashboard = lazy(() => import('../pages/Manage/Dashboard'));
  const ManageInvoices = lazy(() => import('../pages/Manage/Invoices'));
  const ManageCreateInvoice = lazy(() => import('../pages/Manage/CreateInvoice'));
  const ManageDocuments = lazy(() => import('../pages/Manage/Documents'));
  const ManagePayslips = lazy(() => import('../pages/Manage/Payslips'));
  const ManagePayslipView = lazy(() => import('../pages/Manage/PayslipView'));
  const ManageInvoiceScheduleView = lazy(() => import('../pages/Manage/InvoiceScheduleView'));
  const ManageInvoiceScheduleEdit = lazy(() => import('../pages/Manage/InvoiceScheduleEdit'));
  const ManageWallet = lazy(() => import('../pages/Manage/Wallet'));
  const ManageProfile = lazy(() => import('../pages/Manage/Profile'));
  const ManageTax = lazy(() => import('../pages/Manage/Tax'));

  const { theme } = useContext(ThemeContext);

  return (
    <Suspense fallback={<PageLoader />}>
      <div data-theme={theme === 'dark' ? 'dark' : 'light'}>
        <div
          className={theme === 'dark' ? 'dark' : 'light'}
          id='app-container'
          data-theme={theme === 'dark' ? 'dark' : 'light'}
        >
          <LinkProvider component={RouterLink}>
          <Layout>
            <Routes>
              <Route path='/' element={<Overview />} />
              <Route path='/dashboard' element={<Overview />} />
              <Route path='/profile' element={<Profile />} />
              <Route path='/profile/expertise' element={<EditExpertise />} />
              {/* Manage account */}
              <Route path='/manage/onboarding' element={<ManageOnboardingHub />} />
              <Route
                path='/manage/onboarding/personal-profile'
                element={<ManagePersonalProfile />}
              />
              <Route
                path='/manage/onboarding/home-address'
                element={<ManageHomeAddress />}
              />
              <Route
                path='/manage/onboarding/business-information'
                element={<ManageBusinessInformation />}
              />
              <Route
                path='/manage/onboarding/administrative-details'
                element={<ManageAdministrativeDetails />}
              />
              <Route
                path='/manage/onboarding/verification'
                element={<ManageVerification />}
              />
              <Route path='/manage/dashboard' element={<ManageDashboard />} />
              <Route path='/manage/invoices' element={<ManageInvoices />} />
              <Route path='/manage/invoices/create' element={<ManageCreateInvoice />} />
              <Route path='/manage/invoices/schedules/:scheduleId' element={<ManageInvoiceScheduleView />} />
              <Route path='/manage/invoices/schedules/:scheduleId/edit' element={<ManageInvoiceScheduleEdit />} />
              <Route path='/manage/documents' element={<ManageDocuments />} />
              <Route path='/manage/payslips' element={<ManagePayslips />} />
              <Route path='/manage/payslips/:payslipId' element={<ManagePayslipView />} />
              <Route path='/manage/wallet' element={<ManageWallet />} />
              <Route path='/manage/profile' element={<ManageProfile />} />
              <Route path='/manage/tax' element={<ManageTax />} />
              <Route path='/alloffer' element={<AllOffers />} />
              <Route path='/alloffer/:id' element={<MyOffers />} />
              <Route path='/jobs' element={<MatchedJobsTable />} />
              <Route path='/jobs/:id' element={<MatchedJobs />} />
              <Route path='/invites' element={<Invites />} />
              <Route path='/invites/:id' element={<Invite />} />
              <Route path='/project' element={<Project />} />
              <Route path='/projects' element={<ActiveProjects />} />
              <Route path='/project/:id' element={<ActiveProject />} />
              <Route path='/wallet' element={<Wallet />} />
              <Route path='/wallet/withdraw' element={<Withdraw />} />
              <Route path='/wallet/confirm-withdrawal' element={<WalletConfirmation />} />
              <Route path='/transactions' element={<TransactionTable />} />
              <Route path='/support' element={<Support />} />
              <Route path='/content-creator' element={< ContentCreator />} />
              <Route path='/test' element={<Test />} />
              <Route path='*' element={<NotFound />} />


              {/* <Route
                  path='/wallet/withdraw/details'
                  element={<WithdrawalDetails />}
                /> */}
              {/* <Route path='/profile/edit' element={<EditProfile />} /> */}
              {/* <Route path='/test/retake' element={<TakeTest />} /> */}
            </Routes>
          </Layout>
          </LinkProvider>
        </div>
      </div>
    </Suspense>
  );
};

export default AllRoutes;
